import * as Yup from "yup";
import { DateTime } from "luxon";

import {
  generateRecurrenceRuleString,
  FREQUENCIES,
} from "../../../../Recurrence";
import { createForm } from "../../forms";
import { cents } from "../../../utils";

import type { TypeSafeReactLocalization } from "../../../composites";

export type TransferFundsForm = {
  fromAccount: API.AccountId;
  toAccount: API.AccountId;
  amount: Cents;
  agreement: boolean;
  instant: boolean | undefined;
  memo: string | undefined;
  transferUuid: string | undefined;
  date: string | undefined;
  frequency: string | undefined;
  deviceId?: string;
  deviceRequestId?: string;
};

export const translateValidationSchema = (l10n: TypeSafeReactLocalization) =>
  Yup.object().shape({
    fromAccount: Yup.string().required(
      l10n.getString("form-generic-validation-required"),
    ),
    toAccount: Yup.string().required(
      l10n.getString("form-generic-validation-required"),
    ),
    amount: Yup.number()
      .integer(l10n.getString("form-generic-validation-number"))
      .positive(l10n.getString("form-generic-validation-positive-number"))
      .required(l10n.getString("form-generic-validation-required")),
    agreement: Yup.boolean()
      .equals([true], l10n.getString("form-transfers-validation-agreement"))
      .required(l10n.getString("form-generic-validation-required")),
    instant: Yup.boolean().optional(),
    memo: Yup.string()
      .max(128, l10n.getString("form-transfers-validation-memo"))
      .optional(),
    transferUuid: Yup.string().optional(),
    date: Yup.string().optional(),
    frequency: Yup.string().optional(),
    deviceId: Yup.string().optional(),
    deviceRequestId: Yup.string().optional(),
  });

export const initialValues: TransferFundsForm = {
  fromAccount: "" as API.AccountId,
  toAccount: "" as API.AccountId,
  amount: cents(0),
  memo: "",
  agreement: true,
  instant: false,
  transferUuid: undefined,
  date: DateTime.now().toISODate(),
  // set to default "ONCE" which is used in atlas frequency selector in TransferDetails.component.tsx
  frequency: "ONCE",
  deviceId: undefined,
  deviceRequestId: undefined,
};

export const transformTransferFundsFormFieldsToApiFields = ({
  fromAccount: from_account_id,
  toAccount: to_account_id,
  amount,
  agreement,
  instant,
  transferUuid: transfer_uuid,
  date,
  frequency,
  memo,
  deviceId: device_id,
  deviceRequestId: device_request_id,
}: TransferFundsForm) => {
  const fields = {
    amount,
    from_account_id,
    to_account_id,
    agreement,
    instant,
    transfer_uuid,
    memo,
    device_id,
    device_request_id,
  } as API.TransferScheduledRequest;
  const today = DateTime.now().startOf("day");
  const dtDate = date && DateTime.fromISO(date);
  let frequencyVal;
  // atlas and azul frequency selector components return key and value of FREQUENCIES in to in their onChange functions
  // This mapping logic is to support both formats so the same hook can be re-used in both.
  if (frequency && Object.keys(FREQUENCIES).includes(frequency)) {
    frequencyVal = FREQUENCIES[frequency as "ONCE"];
  } else {
    frequencyVal = frequency;
  }
  if (
    !instant &&
    dtDate &&
    (frequencyVal !== FREQUENCIES.ONCE || !dtDate.equals(today))
  ) {
    // only add the recurring rule optional body param for scheduled transfers
    // when all the scheduled fields have been filled in.
    fields.recurring_rule = generateRecurrenceRuleString(frequencyVal, date);
  }

  return fields;
};

export const TransferForm = createForm({
  initialValues,
  translateValidationSchema,
});
